'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import UsdcIcon from '@/assets/icons/usdc.svg';
import TokenInfoCard from '@/components/global/TokenInfoCard';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { getBlockchainPredictionValue } from '@/lib/utils/getBlockchainPredictionValue';
import { I_UserPoolBalance } from '@/server/actions/user/getUserPoolBalances.action';
import { C_DECIMAL_PLACES } from '@/ts/constants/C_DECIMAL_PLACES';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

interface I_InfoCardsSectionProps {
  variant: 'predictions' | 'pools';
  isEmptyState: boolean;
  blockchainData?: T_UserBlockchainData;
  poolBalances?: I_UserPoolBalance[];
}

export function InfoCardsSection({
  variant,
  isEmptyState,
  blockchainData,
  poolBalances
}: I_InfoCardsSectionProps) {
  const t = useTranslations();
  const isPredictions = variant === 'predictions';
  const isPools = variant === 'pools';

  const predictionsStates = useMemo(
    () =>
      isPredictions
        ? [
            {
              titleKey: 'profile.sections.predictions.states.totalWon',
              className: 'bg-[image:var(--gradient-green)]'
            },
            {
              titleKey: 'profile.sections.predictions.states.totalLost',
              className: 'bg-[image:var(--custom-gray-gradient)]'
            },
            {
              titleKey: 'profile.sections.predictions.states.unclaimedWins',
              className: 'bg-[image:var(--gradient-yellow)]'
            }
          ]
        : null,
    [isPredictions]
  );

  // Calculate pools totals from poolBalances
  const poolsTotals = useMemo(() => {
    if (!isPools || !poolBalances || poolBalances.length === 0) {
      return {
        totalTokens: 0,
        totalValue: 0
      };
    }

    let totalTokens = 0;
    let totalValue = 0;

    poolBalances.forEach(balance => {
      const balanceDecimal = parseFloat(balance.balanceDecimal || '0');
      const currentPriceDecimal = parseFloat(
        balance.pool.currentPriceDecimal || '0'
      );

      if (!isNaN(balanceDecimal)) {
        totalTokens += balanceDecimal;
      }

      if (!isNaN(balanceDecimal) && !isNaN(currentPriceDecimal)) {
        totalValue += balanceDecimal * currentPriceDecimal;
      }
    });

    return {
      totalTokens,
      totalValue
    };
  }, [isPools, poolBalances]);

  const infoCards = useMemo(() => {
    if (isPredictions && predictionsStates) {
      return (
        <div className="flex flex-row flex-wrap gap-4 mb-8">
          {predictionsStates.map((state, idx: number) => {
            const count = getBlockchainPredictionValue(
              blockchainData,
              idx,
              isEmptyState
            );

            return (
              <TokenInfoCard
                key={state.titleKey}
                className={cn(
                  state.className,
                  // Mobile: full width (flex-col)
                  // Desktop: first 2 items side by side (w-1/2), 3rd below (w-full)
                  idx < 2 ? 'w-[calc(50%-0.5rem)]!' : 'w-full'
                )}
                title={t(state.titleKey)}
                Icon={UsdcIcon}
                count={formatNumberWithCommasAndDecimals(count, 2)}
                disabled={isEmptyState}
                isClaim={false}
              />
            );
          })}
        </div>
      );
    }

    // For pools, use dynamic data from poolBalances
    if (isPools) {
      const poolsStates = [
        {
          titleKey: 'profile.tokenPoolCard.myTokens',
          className: 'bg-[image:var(--gradient-green)]',
          value: isEmptyState ? 0 : poolsTotals.totalTokens,
          isTokens: true
        },
        {
          titleKey: 'profile.sections.predictions.value',
          className: 'bg-[image:var(--gradient-yellow)]',
          value: isEmptyState ? 0 : poolsTotals.totalValue,
          isTokens: false
        }
      ];

      return (
        <div className="flex flex-col md:flex-row md:flex-wrap gap-4 mb-8">
          {poolsStates.map((state, idx: number) => {
            const formattedValue = state.isTokens
              ? formatNumberWithCommasAndDecimals(
                  state.value.toString(),
                  C_DECIMAL_PLACES
                )
              : `$${formatNumberWithCommasAndDecimals(
                  state.value.toString(),
                  C_DECIMAL_PLACES
                )}`;

            return (
              <TokenInfoCard
                key={state.titleKey}
                className={cn(
                  state.className,
                  // Mobile: full width (flex-col)
                  // Desktop: first 2 items side by side (w-1/2), 3rd below (w-full)
                  idx < 2 ? 'w-full md:w-[calc(50%-0.5rem)]' : 'w-full'
                )}
                title={t(state.titleKey)}
                Icon={UsdcIcon}
                count={formattedValue}
                disabled={isEmptyState}
                isClaim={false}
              />
            );
          })}
        </div>
      );
    }

    // Return null if no data available (should not happen for predictions/pools)
    return null;
  }, [
    isPredictions,
    isPools,
    predictionsStates,
    blockchainData,
    isEmptyState,
    poolBalances,
    poolsTotals,
    t
  ]);

  return infoCards;
}
